import { useLessonPlayer } from '../../hooks/useLessonPlayer.js'
import { FRAMES, LESSON_TITLE } from '../../model/frames.js'
import { applyHandHighlights, backs, fmt } from '../../model/tiles.js'
import { TileRow } from '../../tiles/index.js'
import StatusBadge from '../../components/StatusBadge.jsx'
import DoraChip from '../../components/DoraChip.jsx'
import RiichiStick from '../../components/RiichiStick.jsx'
import UkeireCard from '../../components/UkeireCard.jsx'
import FrameNav from '../../components/FrameNav.jsx'

function SeatArea({ left, top, rotate, discards, label, backs: seatBacks }) {
  return (
    <div style={{ position: 'absolute', left, top, width: 320, height: 130, transform: `rotate(${rotate}deg)` }}>
      <div className="flex h-full flex-col items-center justify-end" style={{ gap: 5 }}>
        <TileRow tiles={discards} width={19} gap={2} className="w-[144px] flex-wrap" />
        <div className="text-[10px] tracking-[0.5px] text-[#cfe0cf]">{label}</div>
        <TileRow tiles={seatBacks} width={15} gap={1} />
      </div>
    </div>
  )
}

export default function TopDownScreen({ frames = FRAMES } = {}) {
  const { frame, index, total, next, prev } = useLessonPlayer(frames)

  const doraTiles = [{ code: frame.doraInd }]
  const heroTiles = applyHandHighlights(frame.hero.hand, frame.hero.hl)
  const heroDraw = frame.hero.draw
    ? [{ code: frame.hero.draw, highlight: !!frame.hero.drawHl }]
    : []
  const heroLabel = `南 You · ${fmt(frame.hero.score)}`

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#efece5] p-6">
      <div className="select-none" style={{ display: 'flex', fontFamily: 'ui-sans-serif, system-ui, sans-serif', outline: 'none', userSelect: 'none' }}>
        <div
          className="relative flex-none"
          style={{ width: 640, height: 640, background: 'radial-gradient(circle at 50% 45%, #2f7a55, #22563d 78%)' }}
        >
          <div
            className="absolute flex flex-col items-center justify-center text-[#dfe8dd]"
            style={{
              left: '50%',
              top: 300,
              width: 196,
              height: 196,
              margin: '-98px 0 0 -98px',
              background: '#1c3f2e',
              borderRadius: 14,
              boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.08)',
              gap: 7,
            }}
          >
            <div className="text-[22px] font-bold tracking-[3px]">{frame.round}</div>
            <div className="text-[10.5px] opacity-75">wall {frame.wall} · honba {frame.honba}</div>
            <DoraChip tiles={doraTiles} width={22} theme="light" />
            {frame.sticks > 0 && (
              <div className="flex items-center gap-[6px]">
                <RiichiStick width={36} theme="light" />
                <span className="text-[11px]">× {frame.sticks}</span>
              </div>
            )}
          </div>

          <SeatArea
            left={160}
            top={20}
            rotate={180}
            discards={frame.top.discards}
            label={`${frame.top.wind} ${frame.top.name} · ${fmt(frame.top.score)}`}
            backs={backs(13)}
          />
          <SeatArea
            left={-75}
            top={255}
            rotate={90}
            discards={frame.left.discards}
            label={`${frame.left.wind} ${frame.left.name} · ${fmt(frame.left.score)}`}
            backs={backs(13)}
          />
          <SeatArea
            left={395}
            top={255}
            rotate={-90}
            discards={frame.right.discards}
            label={`${frame.right.wind} ${frame.right.name} · ${fmt(frame.right.score)}`}
            backs={backs(13)}
          />

          <div
            className="absolute flex flex-col items-center justify-end"
            style={{ left: 20, top: 472, width: 600, height: 156, gap: 6 }}
          >
            <TileRow tiles={frame.hero.discards} width={22} gap={2} className="w-[160px] flex-wrap" />
            <div className="flex items-center gap-2 text-[11px] text-[#e4eee2]">
              <span className="tracking-[0.5px]">{heroLabel}</span>
              {frame.hero.riichi && <StatusBadge kind="riichi" theme="light" />}
              {frame.win && <StatusBadge kind="tsumo" theme="light" />}
            </div>
            <div className="flex items-end" style={{ gap: 2 }}>
              <TileRow tiles={heroTiles} width={32} gap={2} />
              <div style={{ width: 12 }} />
              <TileRow tiles={heroDraw} width={32} />
            </div>
          </div>
        </div>

        <div className="flex flex-none flex-col" style={{ width: 280, padding: '22px 20px', gap: 12 }}>
          <div className="text-[10px] uppercase tracking-[2px] text-[#a39c8c]">Lesson · {LESSON_TITLE}</div>
          <div className="text-[17px] font-bold leading-[1.25] text-[#26221a]">{frame.title}</div>
          <div className="text-[13px] leading-[1.55] text-[#4a463c]">{frame.body}</div>
          {frame.ukeire && (
            <UkeireCard
              theme="light"
              label="WAIT"
              tiles={frame.ukeire.tiles}
              note={frame.ukeire.note}
              width={23}
            />
          )}
          <div className="flex-1" />
          <FrameNav theme="light" prev={prev} next={next} frameNo={index + 1} total={total} />
          <div className="text-[10px] text-[#b3ab99]">Click the table, then use ← → keys</div>
        </div>
      </div>
    </div>
  )
}
